import { CareHomeShortlistData } from '@/careHomeShortlistData';
import { CareCircle, CareCircleMember, Role } from '../careCircle';
import { CareProfileFormEvent, CareProfileLabeledAnswer } from '@/careProfile';

export interface OrganizationType {
  id: string;
  name: string;
}

export interface OrganizationDomain {
  id: string;
  domain: string;
  organizationID: string;
}

export interface Organization {
  id: string;
  name: string;
  inviteKeyName?: string;
  isInviteKeyEnabled?: boolean;
  isInviteCode?: boolean;
  isEmailRegistrationEnabled?: boolean;
  organizationTypeId?: string;
  organizationType?: OrganizationType;
  organizationDomains?: OrganizationDomain[];
  isPrivateEmail?: boolean;
}

export interface OrganizationInfo {
  id: string;
  name: string;
  isInviteKeyEnabled?: boolean;
  isInviteCode?: boolean;
  isEmailRegistrationEnabled?: boolean;
  inviteKeyName?: string;
}

export interface AccountRole {
  id: string;
  name: string;
}

export interface KindeUser {
  id: string;
  email: string;
  kindeID: string;
  picture: string;
  fullName: string;
  firstName: string;
  lastName: string;
  isSuspended?: boolean;
}

export interface AccountConsent {
  accountID?: string;
  consentFormId: string;
  acceptedAt?: Date;
}

export interface Account {
  id: string;
  imgSrc: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  kindeEmail: string;
  kindeAuthType?: string;
  kindeUser?: KindeUser;
  organizationID?: Organization;
  organization: Organization;
  careCircleMembers: CareCircleMember[];
  accountRoleID?: string;
  accountRole: AccountRole;
  isOnboarded?: boolean;
  isOnboardedDashboard?: boolean;
  isOnboardedArticles?: boolean;
  isMigrationEmailReceived?: boolean;
  referLtcCode?: string;
  partnerCode?: string;
  careProfileFormEvents: CareProfileFormEvent[];
  accountConsents?: AccountConsent[];
  isConsent?: boolean;
}

export interface AccountSummary {
  id: string;
  imgSrc: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email?: string;
  inviteKey?: string;
  kindeEmail: string;
  organization: Organization;
  careCircleMembers: CareCircleMember[];
  accountRole: AccountRole;
  isRegistered?: boolean;
  isOnboarded?: boolean;
  isOnboardedDashboard?: boolean;
  isOnboardedArticles?: boolean;
  careProfileFormEvents: CareProfileFormEvent[];
  isConsent?: boolean;
}

export interface CareRecipientSummary {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  careCircleID: string;
  careRecipientAssessmentStatus: string;
  careRecipientAssessmentDate: string;
  careRecipientShortlistData: CareHomeShortlistData[];
  securedRegulatoryID?: string;
  careRecipientFinancialAssessmentStatus: string;
  careRecipientFinancialAssessmentDate: string;
  careRecipientFinancialAssessmentAnswers: CareProfileLabeledAnswer[];
}

export interface CredentialsKinde {
  code: string;
}

export interface AddInviteKeys {
  inviteKeys: string[];
}

export interface AdminAddInviteKeys extends AddInviteKeys {
  organizationID: string;
}

export interface InviteMemberRequest {
  emails: string[];
  isForceSend?: boolean;
}

export interface AdminInviteMemberRequest extends InviteMemberRequest {
  organizationID: string;
}

export interface LdUser {
  key: string;
  name: string;
  email: string;
  kindeEmail: string;
}

export interface LdOrganization {
  key: string;
  name: string;
}

export interface LdContext {
  kind: string;
  user: LdUser;
  organization: LdOrganization;
}

export interface MemberRegisterCredentials {
  email: string;
}

export interface CreateMember {
  caregiverFirstName: string;
  careRecipientFirstName: string;
}

export interface InvitedMember {
  id: string;
  organizationId: string;
  email: string;
  inviteKey: string;
  inviterAccountId?: string;
  inviteSentAt?: string;
  careCircleId?: string;
  careCircle?: CareCircle;
  roleId?: string;
  role?: Role;
  firstName?: string;
  careRecipientAccountRelationshipId?: string;
  isAwaitingFee?: boolean;
}

export enum KindeIdentityType {
  Email = 'email',
  Google = 'oauth2:google',
  Facebook = 'oauth2:facebook',
  Apple = 'oauth2:apple',
}

export const prettyKindeIdentityType = (type?: KindeIdentityType | string) => {
  switch (type) {
    case KindeIdentityType.Email:
      return 'Email';
    case KindeIdentityType.Google:
      return 'Google';
    case KindeIdentityType.Facebook:
      return 'Facebook';
    case KindeIdentityType.Apple:
      return 'Apple';
    default:
      return 'None';
  }
};

export interface PushNotificationToken {
  token: string;
  accountID?: string;
  platform: string;
}
